import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { 
  FileText, Download, Plus, RefreshCw, Calendar, Clock, Mail, 
  AlertTriangle, BarChart3, Shield, GraduationCap, ClipboardCheck,
  Eye, Building2, Briefcase, FileSpreadsheet, Trash2, Play, Settings
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportGeneratorPage = () => {
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const { toast } = useToast();

  const [reportConfig, setReportConfig] = useState({
    name: '',
    report_type: '',
    description: '',
    date_range_type: 'month',
    start_date: '',
    end_date: '',
    include_charts: true,
    include_recommendations: true,
    export_formats: ['pdf']
  });

  const [scheduleConfig, setScheduleConfig] = useState({
    name: '',
    report_type: '',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    time_of_day: '08:00',
    recipients: '',
    export_format: 'pdf',
    include_charts: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchReports();
    fetchScheduledReports();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/report-generator/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to load templates');
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/report-generator/reports`);
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/report-generator/scheduled`);
      if (response.ok) {
        const data = await response.json();
        setScheduledReports(data.scheduled_reports);
      }
    } catch (error) {
      console.error('Failed to load scheduled reports');
    }
  };

  const generateReport = async () => {
    if (!reportConfig.name || !reportConfig.report_type) {
      toast({ title: "Error", description: "Please fill in required fields", variant: "destructive" });
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/report-generator/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportConfig)
      });

      if (response.ok) {
        const report = await response.json();
        toast({ title: "Success", description: `Report "${report.name}" generated successfully` });
        setShowCreateDialog(false);
        fetchReports();
        setReportConfig({
          name: '',
          report_type: '',
          description: '',
          date_range_type: 'month',
          start_date: '',
          end_date: '',
          include_charts: true,
          include_recommendations: true,
          export_formats: ['pdf']
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate report", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const createSchedule = async () => {
    if (!scheduleConfig.name || !scheduleConfig.report_type || !scheduleConfig.recipients) {
      toast({ title: "Error", description: "Please fill in required fields", variant: "destructive" });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/report-generator/scheduled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...scheduleConfig,
          recipients: scheduleConfig.recipients.split(',').map(e => e.trim())
        })
      });

      if (response.ok) {
        toast({ title: "Success", description: "Scheduled report created" });
        setShowScheduleDialog(false);
        fetchScheduledReports();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create schedule", variant: "destructive" });
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      const response = await fetch(`${API_URL}/api/report-generator/scheduled/${scheduleId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast({ title: "Success", description: "Schedule deleted" });
        fetchScheduledReports();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete schedule", variant: "destructive" });
    }
  };

  const getTemplateIcon = (iconName) => {
    const icons = {
      'alert-triangle': AlertTriangle,
      'bar-chart-2': BarChart3,
      'shield-check': Shield,
      'graduation-cap': GraduationCap,
      'clipboard-check': ClipboardCheck,
      'eye': Eye,
      'users': Building2,
      'briefcase': Briefcase,
      'file-text': FileText
    };
    const Icon = icons[iconName] || FileText;
    return <Icon className="w-5 h-5" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="report-generator-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-blue-600" />
            Report Generator
          </h1>
          <p className="text-gray-500 mt-1">Create custom safety reports with multiple export formats</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="schedule-report-btn">
                <Clock className="w-4 h-4" />
                Schedule Report
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Schedule Recurring Report</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Report Name</Label>
                  <Input
                    value={scheduleConfig.name}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, name: e.target.value })}
                    placeholder="Weekly Safety Summary"
                  />
                </div>
                <div>
                  <Label>Report Type</Label>
                  <Select value={scheduleConfig.report_type} onValueChange={(v) => setScheduleConfig({ ...scheduleConfig, report_type: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Frequency</Label>
                    <Select value={scheduleConfig.frequency} onValueChange={(v) => setScheduleConfig({ ...scheduleConfig, frequency: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={scheduleConfig.time_of_day}
                      onChange={(e) => setScheduleConfig({ ...scheduleConfig, time_of_day: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Recipients (comma-separated emails)</Label>
                  <Input
                    value={scheduleConfig.recipients}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, recipients: e.target.value })}
                    placeholder="safety@company.com, manager@company.com"
                  />
                </div>
                <div>
                  <Label>Export Format</Label>
                  <Select value={scheduleConfig.export_format} onValueChange={(v) => setScheduleConfig({ ...scheduleConfig, export_format: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="excel">Excel</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
                <Button onClick={createSchedule}>Create Schedule</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-report-btn">
                <Plus className="w-4 h-4" />
                Generate Report
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Generate New Report</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Report Name *</Label>
                    <Input
                      value={reportConfig.name}
                      onChange={(e) => setReportConfig({ ...reportConfig, name: e.target.value })}
                      placeholder="Q1 Safety Report"
                    />
                  </div>
                  <div>
                    <Label>Report Type *</Label>
                    <Select value={reportConfig.report_type} onValueChange={(v) => setReportConfig({ ...reportConfig, report_type: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={reportConfig.description}
                    onChange={(e) => setReportConfig({ ...reportConfig, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Date Range</Label>
                    <Select value={reportConfig.date_range_type} onValueChange={(v) => setReportConfig({ ...reportConfig, date_range_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="week">Last Week</SelectItem>
                        <SelectItem value="month">Last Month</SelectItem>
                        <SelectItem value="quarter">Last Quarter</SelectItem>
                        <SelectItem value="year">Last Year</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {reportConfig.date_range_type === 'custom' && (
                    <>
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={reportConfig.start_date}
                          onChange={(e) => setReportConfig({ ...reportConfig, start_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={reportConfig.end_date}
                          onChange={(e) => setReportConfig({ ...reportConfig, end_date: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Export Formats</Label>
                  <div className="flex flex-wrap gap-4">
                    {['pdf', 'excel', 'csv', 'json', 'html'].map((format) => (
                      <div key={format} className="flex items-center space-x-2">
                        <Checkbox
                          id={format}
                          checked={reportConfig.export_formats.includes(format)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setReportConfig({ ...reportConfig, export_formats: [...reportConfig.export_formats, format] });
                            } else {
                              setReportConfig({ ...reportConfig, export_formats: reportConfig.export_formats.filter(f => f !== format) });
                            }
                          }}
                        />
                        <label htmlFor={format} className="text-sm font-medium uppercase">{format}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="charts"
                      checked={reportConfig.include_charts}
                      onCheckedChange={(checked) => setReportConfig({ ...reportConfig, include_charts: checked })}
                    />
                    <label htmlFor="charts" className="text-sm">Include Charts</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="recommendations"
                      checked={reportConfig.include_recommendations}
                      onCheckedChange={(checked) => setReportConfig({ ...reportConfig, include_recommendations: checked })}
                    />
                    <label htmlFor="recommendations" className="text-sm">Include Recommendations</label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button onClick={generateReport} disabled={generating}>
                  {generating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  Generate Report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates">Report Templates</TabsTrigger>
          <TabsTrigger value="generated">Generated Reports</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="report-templates">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
                setReportConfig({ ...reportConfig, report_type: template.id, name: template.name });
                setShowCreateDialog(true);
              }}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                      {getTemplateIcon(template.icon)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{template.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {template.default_charts?.slice(0, 2).map((chart) => (
                          <Badge key={chart} variant="secondary" className="text-xs">{chart.replace(/_/g, ' ')}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Generated Reports Tab */}
        <TabsContent value="generated" className="space-y-4">
          <Card data-testid="generated-reports">
            <CardHeader>
              <CardTitle>Recent Reports</CardTitle>
              <CardDescription>Reports generated in the last 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No reports generated yet</p>
                  <Button variant="outline" className="mt-3" onClick={() => setShowCreateDialog(true)}>
                    Generate Your First Report
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div key={report.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-gray-100 rounded">
                          <FileText className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{report.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary">{report.report_type.replace(/_/g, ' ')}</Badge>
                            <span className="text-sm text-gray-500">{formatDate(report.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="gap-1">
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1">
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="scheduled" className="space-y-4">
          <Card data-testid="scheduled-reports">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Scheduled Reports
              </CardTitle>
              <CardDescription>Automatically generated and delivered reports</CardDescription>
            </CardHeader>
            <CardContent>
              {scheduledReports.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No scheduled reports</p>
                  <Button variant="outline" className="mt-3" onClick={() => setShowScheduleDialog(true)}>
                    Schedule a Report
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledReports.map((schedule) => (
                    <div key={schedule.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded ${schedule.status === 'active' ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <Clock className={`w-5 h-5 ${schedule.status === 'active' ? 'text-green-600' : 'text-gray-600'}`} />
                        </div>
                        <div>
                          <h4 className="font-medium">{schedule.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary">{schedule.frequency}</Badge>
                            <span className="text-sm text-gray-500">
                              Next run: {formatDate(schedule.next_run)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                            <Mail className="w-3 h-3" />
                            {schedule.recipients?.join(', ')}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={schedule.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {schedule.status}
                        </Badge>
                        <Button variant="ghost" size="sm">
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteSchedule(schedule.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportGeneratorPage;
